<script lang="ts">
    import { createEventDispatcher } from 'svelte/internal'
    import Fa from 'svelte-fa'
    import { faTriangleExclamation, faFileArrowUp } from '@fortawesome/free-solid-svg-icons'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import Popover from '../../_common/popover.svelte'

    const dispatch = createEventDispatcher()

    // Mesmos limites aplicados no backend (generate-template-from-file.service.ts) — validar aqui
    // também evita fazer upload de um arquivo que sabidamente vai ser rejeitado.
    const MAX_FILE_SIZE_BYTES = 8 * 1024 * 1024
    const ALLOWED_MIME_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp']

    let phase: 'idle' | 'generating' | 'error' = 'idle'
    let errorMessage = ''
    let fileInputEl: HTMLInputElement
    let dragCounter = 0
    let isDragging = false

    function pickFile() {
        fileInputEl?.click()
    }

    function onFileInputChange(event: Event) {
        const input = event.currentTarget as HTMLInputElement
        const file = input.files?.[0]
        input.value = ''
        if (file) handleFile(file)
    }

    function onDrop(event: DragEvent) {
        event.preventDefault()
        dragCounter = 0
        isDragging = false
        const file = event.dataTransfer?.files?.[0]
        if (file) handleFile(file)
    }

    function onDragOver(event: DragEvent) {
        event.preventDefault()
    }

    function onDragEnter(event: DragEvent) {
        event.preventDefault()
        dragCounter++
        isDragging = true
    }

    function onDragLeave(event: DragEvent) {
        event.preventDefault()
        dragCounter--
        if (dragCounter <= 0) {
            dragCounter = 0
            isDragging = false
        }
    }

    function validateFile(file: File): string | null {
        if (!ALLOWED_MIME_TYPES.includes(file.type)) {
            return 'Formato não suportado. Envie um PDF ou uma imagem (PNG, JPEG ou WEBP).'
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
            return 'Arquivo muito grande. O tamanho máximo permitido é 8 MB.'
        }
        return null
    }

    function readAsDataUrl(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result as string)
            reader.onerror = () => reject(reader.error)
            reader.readAsDataURL(file)
        })
    }

    async function handleFile(file: File) {
        const validationError = validateFile(file)
        if (validationError) {
            phase = 'error'
            errorMessage = validationError
            return
        }
        phase = 'generating'
        errorMessage = ''
        try {
            const content = await readAsDataUrl(file)
            const result = await window.api.tools.formsV2.formTemplates.generateFromFile({ filename: file.name, content })
            dispatch('import', result)
        } catch (err: any) {
            phase = 'error'
            errorMessage = typeof err === 'string' ? err : err?.message || 'Não foi possível gerar o modelo a partir deste arquivo.'
        }
    }
</script>

<!-- o Popover do app desenha a cortina, o título e o "x", e já se comporta como folha no celular -->
<Popover title="Gerar modelo com IA" width="560px" height="340px" padding="16px" on:close={() => dispatch('close')}>
    <p class="ai-hint">
        Envie um PDF ou uma imagem (foto/print) de um formulário, contrato ou ficha já existente — a IA interpreta o conteúdo e monta um ponto de partida com os
        blocos correspondentes. O resultado é carregado aqui no editor, sem ser salvo automaticamente: revise e ajuste à vontade antes de salvar.
    </p>

    {#if phase === 'error'}
        <div class="ai-erro">
            <Fa icon={faTriangleExclamation} />
            <span>{errorMessage}</span>
        </div>
    {/if}

    <!-- área de arrastar: mesmo tracejado do ZsheetAdd, com as variáveis de campo (css_z_field.css),
         que já trocam sozinhas entre o tema claro e o escuro -->
    <button
        type="button"
        class="ai-drop"
        class:dragging={isDragging}
        on:click={pickFile}
        on:drop={onDrop}
        on:dragover={onDragOver}
        on:dragenter={onDragEnter}
        on:dragleave={onDragLeave}
    >
        <Fa icon={faFileArrowUp} size="2x" />
        <span class="ai-drop-titulo">Clique para selecionar ou arraste o arquivo aqui</span>
        <span class="ai-drop-dica">PDF, PNG, JPEG ou WEBP — até 8 MB</span>
    </button>
    <input bind:this={fileInputEl} type="file" hidden accept=".pdf,image/png,image/jpeg,image/webp" on:change={onFileInputChange} />

    <LoadSpinner show={phase === 'generating'}>
        Estamos analisando o arquivo e montando o modelo. Isso pode levar alguns minutos em documentos longos — não feche nem atualize esta tela enquanto isso.
    </LoadSpinner>
</Popover>

<style>
    .ai-hint {
        margin: 0px 0px 12px 0px;
        font-size: 13px;
        line-height: 1.4;
        opacity: 0.75;
    }

    .ai-erro {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 12px;
        padding: 10px 12px;
        border-radius: var(--z-field-radius);
        border: 1px solid #d0483e;
        background-color: #d0483e26;
        font-size: 13px;
    }

    .ai-drop {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 32px 16px;
        border: 1px dashed var(--z-field-border);
        border-radius: var(--z-field-radius);
        background: none;
        color: inherit;
        font: inherit;
        cursor: pointer;
        opacity: 0.85;
        transition:
            border-color 0.12s ease,
            background-color 0.12s ease,
            opacity 0.12s ease;
    }
    .ai-drop:hover,
    .ai-drop.dragging {
        border-color: var(--z-field-focus);
        background-color: var(--z-field-bg);
        opacity: 1;
    }
    .ai-drop-titulo {
        font-size: 14px;
        font-weight: 600;
    }
    .ai-drop-dica {
        font-size: 12px;
        opacity: 0.7;
    }
</style>
